import React, { ChangeEvent, useMemo } from 'react';
import Dropdown from '../../../../CommonComponents/Dropdown';
import { DropdownTrigger } from '../../../../OrionOnboarding/OrionOnboardingStyles';
import { HtmlTable, Cell } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { PDFSafeTableHeaderCaption } from '../../../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';
import {
  AssetClassMapping,
  AssetClassMappingWithIndex,
  AssetClassOption,
  getUnknownAssetClassValue,
  MAPPED_TABLE,
  partitionMappings,
  UNKNOWN_TABLE,
} from '../AssetClasses.meta';
import { useDebouncedFilterMappingsBySearch } from '../hooks/useDebouncedFilterMappingsBySearch';

const COLUMN_COUNT = 2;

interface AssetClassesTableProps {
  assetClassesOptions: AssetClassOption[];
  assetClassMappings: AssetClassMapping[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSaveMapping: (
    mappedAssetClasses: { ExternalAssetClassName: string; AssetClass: number },
    originalIndex: number,
  ) => void;
}

export const AssetClassesTable = ({
  assetClassesOptions,
  assetClassMappings,
  searchQuery,
  onSearchChange,
  onSaveMapping,
}: AssetClassesTableProps) => {
  const onMappingChange = (mappingsWithOriginalIndex: AssetClassMappingWithIndex[]) => (
    filteredIndex: number,
    newCurrentAssetClass: AssetClassOption,
  ) => {
    const { ExternalAssetClassName, originalIndex } = mappingsWithOriginalIndex[filteredIndex];

    onSaveMapping(
      { ExternalAssetClassName, AssetClass: newCurrentAssetClass.value },
      originalIndex,
    );
  };

  const partitioned = useMemo(
    () =>
      partitionMappings(
        assetClassMappings,
        getUnknownAssetClassValue(assetClassesOptions),
        assetClassesOptions,
      ),
    [assetClassMappings, assetClassesOptions],
  );

  const {
    searchQuery: localSearchQuery,
    setSearchQuery,
    filteredMappings,
    hasNoSearchResults,
  } = useDebouncedFilterMappingsBySearch(partitioned.otherMappings, searchQuery, onSearchChange);

  const hasUnknownMappings = partitioned.unknownMappings.length > 0;

  return (
    <Box
      display="flex"
      minHeight={300}
      css={({ theme }) => ({
        gap: theme.space['24'],
      })}
    >
      {hasUnknownMappings && (
        <Box minWidth={0} flex={1}>
          <TableContent
            title={UNKNOWN_TABLE.title}
            headerTooltip={UNKNOWN_TABLE.headerTooltip}
            assetClassMappings={partitioned.unknownMappings}
            handleAssetClassMappingChange={onMappingChange(partitioned.unknownMappings)}
            assetClasses={assetClassesOptions}
          />
        </Box>
      )}
      <Box minWidth={0} flex={1}>
        <TableContent
          title={MAPPED_TABLE.title}
          headerTooltip={MAPPED_TABLE.headerTooltip}
          assetClassMappings={filteredMappings}
          handleAssetClassMappingChange={onMappingChange(filteredMappings)}
          assetClasses={assetClassesOptions}
          showSearch
          searchInput={
            <Input
              placeholder="Search..."
              leftIcon="fa fa-search"
              value={localSearchQuery}
              onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(value)
              }
              aria-label="Search asset classes"
            />
          }
          showNoSearchResults={hasNoSearchResults}
        />
      </Box>
    </Box>
  );
};

interface TableContentProps {
  title: string;
  headerTooltip?: string;
  assetClassMappings: AssetClassMappingWithIndex[];
  handleAssetClassMappingChange: (index: number, assetClass: AssetClassOption) => void;
  assetClasses: AssetClassOption[];
  showSearch?: boolean;
  searchInput?: React.ReactNode;
  showNoSearchResults?: boolean;
}

const TableContent = ({
  title,
  headerTooltip,
  assetClassMappings,
  handleAssetClassMappingChange,
  assetClasses,
  showSearch = false,
  searchInput,
  showNoSearchResults = false,
}: TableContentProps) => (
  <HtmlTable variant="bordered" columnSpacing="small" maxHeight="60vh">
    <table style={{ tableLayout: 'fixed' }}>
      <colgroup>
        <col width="50%" />
        <col width="50%" />
      </colgroup>
      <thead style={{ position: 'sticky', zIndex: 2, top: 0 }}>
        <PDFSafeTableHeaderCaption colSpan={COLUMN_COUNT}>
          <Box display="flex" alignItems="center">
            <Text fontWeight={600}>{title}</Text>
            {headerTooltip && (
              <Tooltip content={headerTooltip}>
                <Icon className="far fa-info-circle" fontSize="h6" color="neutral.500" ml="2" />
              </Tooltip>
            )}
          </Box>
        </PDFSafeTableHeaderCaption>
        {showSearch && (
          <tr>
            <th colSpan={COLUMN_COUNT}>{searchInput}</th>
          </tr>
        )}
        <tr>
          <th>
            <Text variant="tableHeader" textAlign="left" ml="40">
              Orion Asset Class
            </Text>
          </th>
          <th>
            <Text variant="tableHeader" textAlign="left">
              Orion Planning
            </Text>
          </th>
        </tr>
      </thead>
      <tbody>
        {showNoSearchResults ? (
          <tr>
            <Cell colSpan={COLUMN_COUNT}>
              <Text color="neutral.500" textAlign="center" py="24">
                No search results found
              </Text>
            </Cell>
          </tr>
        ) : (
          assetClassMappings.map(
            ({ ExternalAssetClassName, currentAssetClass, originalIndex }, i) => (
              <tr key={originalIndex}>
                <Cell isFlex>
                  <Box display="flex" alignItems="center">
                    <Text fontWeight={500} color="neutral.500" mr="16" minWidth={20}>
                      {i + 1}
                    </Text>
                    <Text fontWeight={500} color="neutral.700">
                      {ExternalAssetClassName}
                    </Text>
                  </Box>
                </Cell>
                <Cell>
                  <Dropdown
                    value={currentAssetClass && currentAssetClass.value}
                    items={assetClasses}
                    onChange={updatedAssetClassMapping =>
                      handleAssetClassMappingChange(i, updatedAssetClassMapping)
                    }
                  >
                    <DropdownTrigger appendIcon="fa fa-angle-down">
                      {currentAssetClass && currentAssetClass.label}
                    </DropdownTrigger>
                  </Dropdown>
                </Cell>
              </tr>
            ),
          )
        )}
      </tbody>
    </table>
  </HtmlTable>
);
